import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { colorVariables, fontWeights, votColorVariables } from '@gumtree/ui-library/src/base/theme';

export const ModernFooterBlock = styled.footer`
    background: #f7f7fa;
    color: #0c132d;
    padding: 24px 16px 32px;
`;

export const ModernFooterNav = styled.nav`
    margin: 0 auto;
`;

export const ModernFooterSectionCards = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 24px;
`;

export const ModernFooterDetails = styled.details`
    background: #ffffff;
    border-radius: 8px;
    overflow: hidden;

    &[open] .modern-footer-chevron {
        transform: rotate(225deg);
    }
`;

export const ModernFooterSummary = styled.summary`
    align-items: center;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    list-style: none;
    min-height: 48px;
    padding: 12px;

    &::-webkit-details-marker {
        display: none;
    }
`;

export const ModernFooterCardTitle = styled.span`
    color: #0c132d;
    font-size: 17px;
    font-weight: 600;
    line-height: 24px;
`;

export const ModernFooterChevron = styled.span`
    border: solid #0c132d;
    border-width: 0 2px 2px 0;
    display: inline-block;
    height: 8px;
    margin-right: 5px;
    transform: rotate(45deg);
    transition: transform 0.2s ease;
    width: 8px;
`;

export const ModernFooterCardContent = styled.div`
    padding: 0 12px 12px;
`;

export const ModernFooterContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

export const ModernFooterGroup = styled.section`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

export const ModernFooterGroupTitle = styled.h4`
    color: #56575c;
    font-size: 17px;
    font-weight: 600;
    line-height: 24px;
    margin: 0;
`;

export const ModernFooterAppLinks = styled.div`
    align-items: flex-start;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

export const ModernFooterSocialLinks = styled.div`
    align-items: center;
    display: flex;
    gap: 16px;
    justify-content: space-between;
    max-width: 224px;
`;

export const ModernFooterSocialLink = styled.a`
    display: inline-flex;
    height: 24px;
    width: 24px;

    svg path {
        fill: #0c132d;
    }
`;

export const ModernFooterCopyright = styled.div`
    color: #81838d;
    font-size: 13px;
    letter-spacing: 0.01em;
    line-height: 20px;

    a {
        color: #1b73e8;
    }
`;

export const ModernFooterCopyrightText = styled.span`
    display: block;
`;

export const modernFooterTextLinkStyle = css`
    color: #0c132d;
    display: inline-flex;
    font-size: 13px;
    line-height: 20px;
    padding: 4px 0;
    text-decoration: underline;
`;

export const modernFooterCardLinkStyle = css`
    color: #1b73e8;
    display: block;
    font-size: 15px;
    line-height: 22px;
    padding: 6px 0;
    text-decoration: none;
`;

export const modernFooterBadgeLinkStyle = css`
    display: inline-flex;
    height: 41px;
    width: 140px;

    svg {
        height: 100%;
        width: 100%;
    }
`;

export const VotFooterBlock = styled.footer<{ $isDesktop?: boolean }>`
    background: ${votColorVariables.footerBackground};
    color: ${votColorVariables.neutralLight};
    font-family: 'Readex Pro', sans-serif;
    padding: 40px 16px 32px;

    @media (min-width: 48em) {
        padding: 24px 32px;
    }
`;

export const VotFooterNav = styled.nav<{ $isDesktop?: boolean }>`
    margin: 0 auto;
    max-width: ${(props) => (props.$isDesktop ? '1350px' : '1122.645px')};

    @media (min-width: 48em) {
        ${(props) =>
            props.$isDesktop &&
            css`
                display: flex;
                flex-direction: column;
                gap: 10px;
            `}
    }
`;

export const VotFooterPrimary = styled.div<{ $isDesktop?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 28px;

    @media (min-width: 48em) {
        ${(props) =>
            props.$isDesktop
                ? css`
                      align-items: flex-start;
                      flex-direction: row;
                      height: 196px;
                      justify-content: space-between;
                  `
                : css`
                      align-items: flex-start;
                      flex-direction: row;
                      justify-content: space-between;
                  `}
    }
`;

export const VotFooterSectionCards = styled.div<{ $isDesktop?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 4px;

    @media (min-width: 48em) {
        flex: 1;
        flex-direction: row;
        gap: 16px;
    }
`;

export const VotFooterDetails = styled.details<{ $isDesktop?: boolean }>`
    background: transparent;

    &[open] .vot-footer-chevron {
        transform: rotate(225deg);
    }

    ${ModernFooterCardContent} {
        padding: 0 4px 12px;
    }

    @media (min-width: 48em) {
        ${(props) =>
            props.$isDesktop
                ? css`
                      align-self: stretch;
                      display: flex;
                      flex: 1;
                      flex-direction: column;
                      justify-content: flex-start;
                  `
                : css`
                      flex: 1;
                  `}

        ${ModernFooterCardContent} {
            padding: ${(props) => (props.$isDesktop ? '6px 0 0' : '0')};
        }
    }
`;

export const VotFooterSummary = styled.summary<{ $isDesktop?: boolean }>`
    align-items: center;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    list-style: none;
    min-height: 28px;
    padding: 12px 4px;

    &::-webkit-details-marker {
        display: none;
    }

    @media (min-width: 48em) {
        min-height: 22px;
        padding: ${(props) => (props.$isDesktop ? '0' : '0 0 12px')};
        pointer-events: none;
    }
`;

export const VotFooterTitle = styled.span<{ $isDesktop?: boolean }>`
    color: ${colorVariables.backgroundNeutral};
    font-size: 18px;
    font-weight: ${fontWeights.bold};
    line-height: 28px;

    @media (min-width: 48em) {
        font-size: ${(props) => (props.$isDesktop ? '16px' : '18px')};
        font-weight: ${(props) => (props.$isDesktop ? fontWeights.lightBold : fontWeights.bold)};
        line-height: ${(props) => (props.$isDesktop ? '24px' : '28px')};
    }
`;

export const VotFooterChevron = styled.span`
    border: solid ${votColorVariables.textMuted};
    border-width: 0 1.5px 1.5px 0;
    display: inline-block;
    height: 8px;
    margin-right: 5px;
    transform: rotate(45deg);
    transition: transform 0.2s ease;
    width: 8px;

    @media (min-width: 48em) {
        display: none;
    }
`;

export const VotFooterApps = styled.section<{ $isDesktop?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 12px;

    @media (min-width: 48em) {
        ${(props) =>
            props.$isDesktop
                ? css`
                      align-items: flex-start;
                      flex: 0 0 165px;
                      justify-content: flex-start;
                  `
                : css`
                      flex: 0 0 165px;
                  `}
    }
`;

export const VotFooterGroupTitle = styled.h4<{ $isDesktop?: boolean }>`
    color: ${votColorVariables.neutralLight};
    font-size: 18px;
    font-weight: ${fontWeights.bold};
    line-height: 28px;
    margin: 0;

    @media (min-width: 48em) {
        color: ${(props) =>
            props.$isDesktop ? colorVariables.backgroundNeutral : votColorVariables.neutralLight};
        font-size: ${(props) => (props.$isDesktop ? '16px' : '18px')};
        font-weight: ${(props) => (props.$isDesktop ? fontWeights.lightBold : fontWeights.bold)};
        line-height: ${(props) => (props.$isDesktop ? '24px' : '28px')};
    }
`;

export const VotFooterCommunityTitle = styled.h4<{ $isDesktop?: boolean }>`
    color: ${votColorVariables.neutralLight};
    font-size: 18px;
    font-weight: ${fontWeights.bold};
    line-height: 28px;
    margin: 0;

    @media (min-width: 48em) {
        color: ${(props) =>
            props.$isDesktop ? colorVariables.backgroundNeutral : votColorVariables.neutralLight};
        font-size: ${(props) => (props.$isDesktop ? '14px' : '18px')};
        font-weight: ${(props) => (props.$isDesktop ? fontWeights.medium : fontWeights.bold)};
        line-height: ${(props) => (props.$isDesktop ? '22px' : '28px')};
    }
`;

export const VotFooterAppLinks = styled.div`
    align-items: flex-start;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

export const VotFooterDesktopAppBadge = styled.span<{
    $app: 'app-store' | 'google-play';
}>`
    align-items: center;
    border: 0.5px solid
        ${(props) =>
            props.$app === 'app-store'
                ? votColorVariables.footerDesktopAppStoreBorder
                : votColorVariables.footerDesktopLegalText};
    border-radius: 8px;
    box-sizing: border-box;
    color: ${colorVariables.backgroundNeutral};
    display: flex;
    gap: 5px;
    height: 32px;
    padding: 4px 16px;
    width: 122px;

    > svg {
        flex: 0 0 auto;
        height: ${(props) => (props.$app === 'app-store' ? '16px' : '17px')};
        width: ${(props) => (props.$app === 'app-store' ? '14px' : '16px')};
    }
`;

export const VotFooterDesktopAppBadgeText = styled.span`
    display: flex;
    flex-direction: column;
    gap: 1px;
    line-height: 1;
    white-space: nowrap;

    > span {
        font-size: 6.5px;
        font-weight: ${fontWeights.medium};
        letter-spacing: -0.0929px;
    }

    > strong {
        font-family: Inter, sans-serif;
        font-size: 12px;
        font-weight: ${fontWeights.lightBold};
        line-height: 16px;
    }
`;

export const VotFooterDesktopDivider = styled.div<{ $isDesktop?: boolean }>`
    display: none;

    @media (min-width: 48em) {
        display: block;
        ${(props) =>
            props.$isDesktop
                ? css`
                      background: ${votColorVariables.footerDesktopDivider};
                      height: 1px;
                      margin: 0 auto;
                      max-width: 1350px;
                      width: 100%;
                  `
                : css`
                      background: ${votColorVariables.footerDivider};
                      height: 2px;
                      margin: 16px 0;
                  `}
    }
`;

export const VotFooterSecondary = styled.div<{ $isDesktop?: boolean; hasPartners: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 28px;

    @media (min-width: 48em) {
        ${(props) =>
            props.$isDesktop
                ? css`
                      align-items: center;
                      height: 100px;
                      justify-content: center;
                      padding: 0 100px;
                      position: relative;
                  `
                : css`
                      align-items: flex-start;
                      flex-direction: row;
                      justify-content: ${props.hasPartners ? 'space-between' : 'center'};
                  `}
    }
`;

export const VotFooterCommunity = styled.section<{ $isDesktop?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 12px;

    @media (min-width: 48em) {
        align-items: ${(props) => (props.$isDesktop ? 'center' : 'flex-start')};
        gap: ${(props) => (props.$isDesktop ? '16px' : '8px')};
        width: ${(props) => (props.$isDesktop ? '100%' : 'auto')};
    }
`;

export const VotFooterSocialLinks = styled.div<{ $isDesktop?: boolean }>`
    align-items: center;
    border-bottom: 1px solid ${votColorVariables.footerBorder};
    border-top: 1px solid ${votColorVariables.footerBorder};
    display: flex;
    gap: 16px;
    justify-content: center;
    padding: 24px 0;

    @media (min-width: 48em) {
        border: 0;
        gap: ${(props) => (props.$isDesktop ? '8px' : '16px')};
        justify-content: ${(props) => (props.$isDesktop ? 'center' : 'flex-start')};
        padding: 0;
    }
`;

export const VotFooterSocialLink = styled.a<{ $isDesktop?: boolean }>`
    display: inline-flex;
    height: 24px;
    width: 24px;

    svg {
        margin: auto;
    }

    ${(props) =>
        !props.$isDesktop &&
        css`
            svg path {
                fill: ${colorVariables.backgroundNeutral};
            }
        `}
`;

export const VotFooterPartners = styled.section<{ $isDesktop?: boolean }>`
    align-items: center;
    display: flex;
    flex-direction: column;
    gap: 8px;

    @media (min-width: 48em) {
        ${(props) =>
            props.$isDesktop &&
            css`
                position: absolute;
                right: 100px;
            `}
    }
`;

export const VotFooterPartnerLink = styled.a`
    background: ${votColorVariables.textMuted};
    border-radius: 2px;
    color: ${colorVariables.backgroundNeutral};
    font-size: 14px;
    font-weight: 330;
    line-height: 22px;
    padding: 4px 8px;
    text-decoration: none;
`;

export const VotFooterCopyright = styled.div<{ $isDesktop?: boolean }>`
    color: ${votColorVariables.textMuted};
    font-size: 14px;
    font-weight: ${fontWeights.normal};
    letter-spacing: 0;
    line-height: 22px;
    margin-top: 32px;

    a {
        color: ${votColorVariables.footerLink};
        text-decoration: none;
    }

    @media (min-width: 48em) {
        ${(props) =>
            props.$isDesktop
                ? css`
                      color: ${votColorVariables.footerDesktopLegalText};
                      display: flex;
                      flex-direction: column;
                      font-size: 12px;
                      font-weight: ${fontWeights.medium};
                      gap: 24px;
                      line-height: 18px;
                      margin: 0;
                      max-width: none;
                      text-align: center;

                      ${ModernFooterCopyrightText} {
                          display: flex;
                          flex-direction: column;
                          gap: 20px;
                          padding: 16px 0;
                      }

                      ${ModernFooterCopyrightText} > span {
                          display: block;
                          margin: 0 auto;
                          max-width: 600px;
                      }

                      ${ModernFooterCopyrightText} br {
                          display: none;
                      }
                  `
                : css`
                      margin: 34px auto 0;
                      max-width: 695px;
                      text-align: center;
                  `}
    }
`;

export const VotFooterCopyrightLinks = styled.div<{ $isDesktop?: boolean }>`
    @media (min-width: 48em) {
        ${(props) =>
            props.$isDesktop &&
            css`
                color: ${votColorVariables.footerDesktopLink};
                font-size: 14px;
                font-weight: ${fontWeights.medium};
                line-height: 22px;

                a {
                    color: inherit;
                    text-decoration: none;
                }
            `}
    }
`;

export const getVotFooterTextLinkStyle = (isDesktop = false) => css`
    color: ${votColorVariables.footerLink};
    display: block;
    font-family: 'Readex Pro', sans-serif;
    font-size: 16px;
    font-weight: 330;
    line-height: 24px;
    padding: 2px 0;
    text-decoration: none;

    @media (min-width: 48em) {
        color: ${isDesktop ? votColorVariables.footerDesktopLink : votColorVariables.footerLink};
        font-size: ${isDesktop ? '14px' : '16px'};
        font-weight: ${isDesktop ? fontWeights.medium : fontWeights.normal};
        line-height: ${isDesktop ? '22px' : '24px'};
        padding: ${isDesktop ? '4px 0' : '2px 0'};
    }
`;

export const getVotFooterBadgeLinkStyle = (isDesktop = false) => css`
    display: inline-flex;
    height: ${isDesktop ? '32px' : '42px'};
    width: ${isDesktop ? '122px' : '140px'};

    > svg {
        height: 100%;
        width: 100%;
    }
`;

export const votFooterBadgeLinkStyle = getVotFooterBadgeLinkStyle();
